import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { t } from "../../i18n";
import { EndpointDetail } from "./BackendApiCoverageDetailPage";
import { backendApiCoverageStyles as styles } from "./backend-api-coverage-styles";
import type {
  BackendApiCoverageSnapshot,
  BackendApiEndpoint,
  BackendApiTestCase,
} from "./backend-api-coverage-types";

interface BackendApiBrowserProps {
  selectedSlug?: string;
  snapshot: BackendApiCoverageSnapshot;
}

export function BackendApiBrowser({
  selectedSlug,
  snapshot,
}: BackendApiBrowserProps): React.JSX.Element {
  const [query, setQuery] = useState("");
  const selectedEndpoint = useMemo(
    () => selectedEndpointFor(snapshot.endpoints, selectedSlug),
    [selectedSlug, snapshot.endpoints],
  );
  const tests = useMemo(
    () => endpointTests(selectedEndpoint, snapshot.tests),
    [selectedEndpoint, snapshot.tests],
  );
  const groups = useMemo(
    () => groupEndpoints(filterTreeEndpoints(snapshot.endpoints, query)),
    [query, snapshot.endpoints],
  );
  return (
    <section style={styles.browserShell}>
      <aside style={styles.apiTreePanel}>
        <div style={styles.apiTreeHeader}>
          <h2 style={styles.sectionTitle}>{t("api_coverage.api_modules")}</h2>
          <span style={styles.meta}>
            {t("api_coverage.filtered_count", {
              visible: groups.reduce(
                (sum, group) => sum + group.endpoints.length,
                0,
              ),
              total: snapshot.endpoints.length,
            })}
          </span>
        </div>
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("api_coverage.tree_search_placeholder")}
          style={styles.input}
        />
        <nav style={styles.apiTree} aria-label={t("api_coverage.api_modules")}>
          {groups.map((group) => (
            <details
              key={group.module}
              open={
                query.trim().length > 0 ||
                moduleHasSelection(group, selectedEndpoint)
              }
            >
              <summary style={styles.apiTreeModule}>
                {group.module}
                <span style={styles.apiTreeCount}>
                  {group.endpoints.length}
                </span>
              </summary>
              <div style={styles.apiTreeEndpointList}>
                {group.endpoints.map((endpoint) => (
                  <EndpointTreeLink
                    key={endpoint.id}
                    endpoint={endpoint}
                    selected={endpoint.slug === selectedEndpoint?.slug}
                  />
                ))}
              </div>
            </details>
          ))}
        </nav>
      </aside>
      <main style={styles.apiDetailPanel}>
        {selectedEndpoint ? (
          <EndpointDetail endpoint={selectedEndpoint} tests={tests} />
        ) : (
          <p style={styles.meta}>{t("api_coverage.endpoint_not_found")}</p>
        )}
      </main>
    </section>
  );
}

function EndpointTreeLink({
  endpoint,
  selected,
}: {
  endpoint: BackendApiEndpoint;
  selected: boolean;
}): React.JSX.Element {
  return (
    <Link
      to={`/system/backend-api-coverage/${endpoint.slug}`}
      style={{
        ...styles.apiTreeEndpoint,
        ...(selected ? styles.apiTreeEndpointActive : undefined),
      }}
    >
      <span style={styles.apiTreeEndpointTop}>
        <span style={styles.methodBadge}>{endpoint.method}</span>
        <span style={styles.apiTreePath}>{endpoint.path}</span>
      </span>
      <span style={styles.apiTreeTitle}>{endpoint.title}</span>
    </Link>
  );
}

function selectedEndpointFor(
  endpoints: BackendApiEndpoint[],
  slug?: string,
): BackendApiEndpoint | null {
  return (
    endpoints.find((endpoint) => endpoint.slug === slug) ?? endpoints[0] ?? null
  );
}

function filterTreeEndpoints(
  endpoints: BackendApiEndpoint[],
  query: string,
): BackendApiEndpoint[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return endpoints;
  return endpoints.filter((endpoint) =>
    `${endpoint.method} ${endpoint.path}\n${endpoint.title}\n${endpoint.module}`
      .toLowerCase()
      .includes(normalized),
  );
}

function groupEndpoints(endpoints: BackendApiEndpoint[]): ApiModuleGroup[] {
  const groups = new Map<string, BackendApiEndpoint[]>();
  for (const endpoint of endpoints) {
    const list = groups.get(endpoint.module) ?? [];
    list.push(endpoint);
    groups.set(endpoint.module, list);
  }
  return [...groups.entries()].map(([module, groupEndpointsForModule]) => ({
    module,
    endpoints: groupEndpointsForModule,
  }));
}

function moduleHasSelection(
  group: ApiModuleGroup,
  selectedEndpoint: BackendApiEndpoint | null,
): boolean {
  return group.endpoints.some(
    (endpoint) => endpoint.slug === selectedEndpoint?.slug,
  );
}

function endpointTests(
  endpoint: BackendApiEndpoint | null,
  tests: BackendApiTestCase[],
): BackendApiTestCase[] {
  if (!endpoint) return [];
  const ids = new Set(endpoint.tests.map((item) => item.test_id));
  return tests.filter((testCase) => ids.has(testCase.id));
}

interface ApiModuleGroup {
  endpoints: BackendApiEndpoint[];
  module: string;
}
